import { z } from "zod";

/**
 * `GET /api/v1/me/notifications`'s answer: **the bell's rows and its badge, one contract for the door and for every
 * client** (`CONTRACT-NOTIFICATIONS`, A48: a stub speaks the provider's contract, never the client's expectation).
 *
 * Until this file the door answered `Promise<unknown>`: the dealer's bell and the bank's bell each declared the shape they
 * EXPECTED (`DealerNotice`, an inline `{ id?: unknown; … }`), and the dealer's positioning check stubbed the door with the
 * dealer's own type, so a stub could describe a body the API never sends. The API's controller is typed by
 * {@link NotificationsAnswer}; the apps and the stubs import the same name; and an integration spec parses the door's REAL
 * answer with {@link notificationsAnswerSchema}, so a field added on one side and not the other fails there.
 *
 * Strict on purpose: an extra key is a mismatch, not a tolerated surplus. The two sibling doors under the same path
 * (`/unread-count`, `/{id}/read`) answer `{ unread }` and `{ read: true }`; `GENERIC-DOOR-SWEEP`, Arc 1 gave them their
 * own names below, {@link unreadCountAnswerSchema} and {@link readMarkAnswerSchema}.
 */
export const notificationRowSchema = z.strictObject({
  id: z.string(),
  /** The writer's own tag (`premiere-lecture-prete`, `piece-vendeur-relancee`, …): a client branches on it, never on the words. */
  kind: z.string(),
  title: z.string(),
  body: z.string(),
  /** Where the row opens, as the writer gave it; `null` when it opens nothing. */
  linkPath: z.string().nullable(),
  subjectType: z.string().nullable(),
  subjectId: z.string().nullable(),
  /** The instant she read it, ISO 8601; `null` while unread. Set only by her own act, never by a render. */
  readAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});

export const notificationsAnswerSchema = z.strictObject({
  /** Her rows, newest first, at most fifty. */
  notifications: z.array(notificationRowSchema),
  /** Counted over ALL her rows (`read_at IS NULL`), not over the fifty above: the badge and the list say one number. */
  unread: z.number().int().min(0),
});

export type NotificationRow = z.infer<typeof notificationRowSchema>;
export type NotificationsAnswer = z.infer<typeof notificationsAnswerSchema>;

/** `GET /api/v1/me/notifications/unread-count`: the badge's number, counted over all her rows, cached fifteen seconds. */
export const unreadCountAnswerSchema = z.strictObject({
  unread: z.number().int().min(0),
});

/** `POST /api/v1/me/notifications/{id}/read`: always `{ read: true }`, whether the row was just read or already was. */
export const readMarkAnswerSchema = z.strictObject({
  read: z.literal(true),
});

/**
 * `POST /api/v1/me/notifications/read` (`P1-NTF-008`, `MARK-ALL-READ`, D-1028-1): the signed-in user's own unread notifications, all read by
 * ONE act. `marked` is the number this call marked (0 on a second call: nothing changes), `unread` is the badge's number re-derived in the
 * same transaction, so the browser computes neither. 04 §4.2.13 writes this door as `POST /api/v1/notifications/read`, « Mark a set as
 * read »; the path sits under `me/` as the module's convention, both texts kept.
 */
export const readAllAnswerSchema = z.strictObject({
  marked: z.number().int().min(0),
  unread: z.number().int().min(0),
});

export type UnreadCountAnswer = z.infer<typeof unreadCountAnswerSchema>;
export type ReadMarkAnswer = z.infer<typeof readMarkAnswerSchema>;
export type ReadAllAnswer = z.infer<typeof readAllAnswerSchema>;
